import * as React from 'react';

import { cn } from '@/lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface DatePickerProps {
  /** Date value as YYYY-MM-DD string (same format as native input[type="date"]) */
  value?: string;
  /** Called with YYYY-MM-DD string when date changes */
  onChange?: (value: string) => void;
  /** Minimum selectable date as YYYY-MM-DD string */
  min?: string;
  /** Maximum selectable date as YYYY-MM-DD string */
  max?: string;
  /** Placeholder text when no date is selected */
  placeholder?: string;
  /** Disable the picker */
  disabled?: boolean;
  /** Additional className for the container */
  className?: string;
  /** ID for the container */
  id?: string;
}

const MONTHS = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

function getDaysInMonth(month: number, year: number): number {
  if (!month || !year) return 31;
  return new Date(year, month, 0).getDate();
}

function DatePicker({
  value,
  onChange,
  min,
  max,
  placeholder: _placeholder = 'Pick a date',
  disabled = false,
  className,
  id,
}: DatePickerProps) {
  const currentYear = new Date().getFullYear();

  // Parse value into parts
  const parsed = React.useMemo(() => {
    if (!value) return { year: '', month: '', day: '' };
    const parts = value.split('-');
    if (parts.length !== 3) return { year: '', month: '', day: '' };
    return { year: parts[0], month: parts[1], day: parts[2] };
  }, [value]);

  // Parse min/max bounds
  const minParsed = React.useMemo(() => {
    if (!min) return null;
    const parts = min.split('-');
    if (parts.length !== 3) return null;
    return { year: parseInt(parts[0]), month: parseInt(parts[1]), day: parseInt(parts[2]) };
  }, [min]);

  const maxParsed = React.useMemo(() => {
    if (!max) return null;
    const parts = max.split('-');
    if (parts.length !== 3) return null;
    return { year: parseInt(parts[0]), month: parseInt(parts[1]), day: parseInt(parts[2]) };
  }, [max]);

  // Generate year options (current year down to 100 years ago)
  const years = React.useMemo(() => {
    const maxYear = maxParsed ? maxParsed.year : currentYear;
    const minYear = minParsed ? minParsed.year : currentYear - 100;
    const result: string[] = [];
    for (let y = maxYear; y >= minYear; y--) {
      result.push(String(y));
    }
    return result;
  }, [currentYear, minParsed, maxParsed]);

  // Days in selected month/year
  const daysInMonth = React.useMemo(() => {
    const m = parseInt(parsed.month) || 1;
    const y = parseInt(parsed.year) || currentYear;
    return getDaysInMonth(m, y);
  }, [parsed.month, parsed.year, currentYear]);

  const days = React.useMemo(() => {
    const result: string[] = [];
    for (let d = 1; d <= daysInMonth; d++) {
      result.push(String(d).padStart(2, '0'));
    }
    return result;
  }, [daysInMonth]);

  const emitChange = React.useCallback(
    (year: string, month: string, day: string) => {
      if (!onChange) return;
      if (year && month && day) {
        // Clamp day to valid range for the month
        const maxDay = getDaysInMonth(parseInt(month), parseInt(year));
        const clampedDay = Math.min(parseInt(day), maxDay);
        onChange(`${year}-${month}-${String(clampedDay).padStart(2, '0')}`);
      }
    },
    [onChange],
  );

  const handleMonthChange = (month: string) => {
    if (parsed.year && parsed.day) {
      emitChange(parsed.year, month, parsed.day);
    } else if (parsed.year) {
      // Partial — set month, wait for day
      emitChange(parsed.year, month, '01');
    } else {
      // No year yet — default to current year
      emitChange(String(currentYear), month, parsed.day || '01');
    }
  };

  const handleDayChange = (day: string) => {
    if (parsed.year && parsed.month) {
      emitChange(parsed.year, parsed.month, day);
    } else {
      emitChange(parsed.year || String(currentYear), parsed.month || '01', day);
    }
  };

  const handleYearChange = (year: string) => {
    if (parsed.month && parsed.day) {
      emitChange(year, parsed.month, parsed.day);
    } else if (parsed.month) {
      emitChange(year, parsed.month, '01');
    } else {
      emitChange(year, parsed.month || '01', parsed.day || '01');
    }
  };

  const hasValue = parsed.year && parsed.month && parsed.day;

  return (
    <div id={id} className={cn('flex gap-2', className)}>
      {/* Month */}
      <Select
        value={parsed.month || undefined}
        onValueChange={handleMonthChange}
        disabled={disabled}
      >
        <SelectTrigger
          className={cn(
            'flex-[3] min-w-0',
            !hasValue && 'text-muted-foreground',
          )}
        >
          <SelectValue placeholder="Month" />
        </SelectTrigger>
        <SelectContent>
          {MONTHS.map((m) => (
            <SelectItem key={m.value} value={m.value}>
              {m.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Day */}
      <Select
        value={parsed.day || undefined}
        onValueChange={handleDayChange}
        disabled={disabled}
      >
        <SelectTrigger
          className={cn(
            'flex-[1.5] min-w-0',
            !hasValue && 'text-muted-foreground',
          )}
        >
          <SelectValue placeholder="Day" />
        </SelectTrigger>
        <SelectContent>
          {days.map((d) => (
            <SelectItem key={d} value={d}>
              {parseInt(d)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Year */}
      <Select
        value={parsed.year || undefined}
        onValueChange={handleYearChange}
        disabled={disabled}
      >
        <SelectTrigger
          className={cn(
            'flex-[2] min-w-0',
            !hasValue && 'text-muted-foreground',
          )}
        >
          <SelectValue placeholder="Year" />
        </SelectTrigger>
        <SelectContent>
          {years.map((y) => (
            <SelectItem key={y} value={y}>
              {y}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export { DatePicker };
